<template>
	<Installation />
</template>

<script setup lang="ts">
import {
	BASE_URL,
	COMPANY_NAME
} from '@/constants/index.js'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { OG_IMAGE_DEFAULT } from '@/constants/og-image'

const route = useRoute()

const { t } = useI18n({
	useScope: 'global'
})

const title = t('residential.meta-title')
const description = t('residential.meta-description')

useHead({
	title: t('residential.meta-title'),
	meta: [
		{ name: 'description',
			content: t('residential.meta-description', { COMPANY_NAME }) },
		{ name: 'url',
			content: `${BASE_URL}/installation-overview` }
	],
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicons/favicon.ico'
		}
	]
})
useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogUrl: () => BASE_URL,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => '/images/og-image-fallback.png',
	twitterCard: () => 'summary_large_image'
})
 import.meta.prerender && defineOgImage('Default', OG_IMAGE_DEFAULT)

useJsonld({
	'@context': 'https://schema.org',
	'@type': 'HowTo',
	'name': 'Fiber Internet Installation Process',
	'description': 'Complete guide to our fiber internet installation process from mainline construction to online activation',
	'estimatedCost': {
		'@type': 'MonetaryAmount',
		'currency': 'USD',
		'value': '0'
	},
	'totalTime': 'PT2W',
	'supply': [
		{
			'@type': 'HowToSupply',
			'name': 'Fiber optic cable'
		},
		{
			'@type': 'HowToSupply',
			'name': 'Utility box'
		},
		{
			'@type': 'HowToSupply',
			'name': 'Modem/Router equipment'
		}
	],
	'step': [
		{
			'@type': 'HowToStep',
			'position': 1,
			'name': 'Main Line Construction',
			'text': 'To connect your neighborhood to our internet backbone, the Mainline Construction process involves acquiring the necessary permits from local governments and homeowner associations, while also completing the necessary backbone construction throughout your neighborhood.',
			'url': `${BASE_URL}${route.fullPath}#mainline-construction`
		},
		{
			'@type': 'HowToStep',
			'position': 2,
			'name': 'Home Site Survey',
			'text': 'Our technician will meet with you to determine the optimal path to run fiber from the street to the exterior of your residence. The proper location is chosen based upon where you would like your service to be connected inside and where our utility box at the road is placed, avoiding existing utilities and known obstacles as much as possible.',
			'url': `${BASE_URL}${route.fullPath}#home-site-survey`
		},
		{
			'@type': 'HowToStep',
			'position': 3,
			'name': 'Yard Trenching',
			'text': 'Along the agreed upon path and after we verify existing in-ground infrastructure as much as possible, our team will utilize a vibratory plow or direction drill to bury your new line from the road to the exterior of your house with minimal disturbance. The line will be connected to the utility box we placed on the outside of your house during the inside installation, completing the construction process on your property.',
			'url': `${BASE_URL}${route.fullPath}#yard-trenching`
		},
		{
			'@type': 'HowToStep',
			'position': 4,
			'name': 'Interior Home Installation',
			'text': 'We will install a small utility box on the exterior of your home and cleanly run fiber inside to a new wall plate. At this time, we will deliver a non-wireless modem that will provide service for your home. Optionally, if you choose to purchase or rent a wireless router from us or wish to hire us to run additional cabling for you, we will install it at this time.',
			'url': `${BASE_URL}${route.fullPath}#interior-installation`
		},
		{
			'@type': 'HowToStep',
			'position': 5,
			'name': 'Fiber Splicing',
			'text': 'The final stage of your installation happens at the roadway by a member of our splicing team. Once completed, our team will verify your service is online and will reach out to help you start using your new, high-speed internet!',
			'url': `${BASE_URL}${route.fullPath}#fiber-splicing`
		},
		{
			'@type': 'HowToStep',
			'position': 6,
			'name': 'Online Activation',
			'text': 'After the construction of your fiber internet is finished, you will receive a notification to activate your service by confirming your account and billing information online.',
			'url': `${BASE_URL}${route.fullPath}#online-activation`
		}
	]
})
</script>
